import { useState } from 'react';
import { ArrowLeft, Monitor, PanelTop, Pin, Clock, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { getDesktopPrefs, setDesktopPrefs, isMacAppStore } from '@/lib/desktopBridge';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from 'sonner';
import type { DesktopPrefs } from '@/types/electron';

interface DesktopSettingsSectionProps {
  onBack: () => void;
}

/**
 * Desktop-only widget preferences. Changes apply instantly (persisted to
 * localStorage and pushed to the Electron main process via IPC).
 */
export function DesktopSettingsSection({ onBack }: DesktopSettingsSectionProps) {
  const [prefs, setPrefs] = useState<DesktopPrefs>(getDesktopPrefs);
  const { restorePurchases } = useSubscription();
  const [isRestoring, setIsRestoring] = useState(false);
  const showRestore = isMacAppStore();

  const update = (patch: Partial<DesktopPrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setDesktopPrefs(next);
  };

  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      const result = await restorePurchases();
      if (result.success) {
        toast.success('Purchases restored');
      } else {
        toast.error(result.error || 'Nothing to restore');
      }
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-semibold">Desktop</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Monitor className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-medium">Menu bar & floating timer</h3>
        </div>
        <p className="text-sm text-muted-foreground -mt-3">
          Control how Luma's timer appears outside the main window.
        </p>

        <div className="space-y-4">
          {/* Floating timer */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-3">
              <PanelTop className="h-5 w-5 text-primary mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="floating-enabled" className="font-medium">Floating timer</Label>
                <p className="text-sm text-muted-foreground">
                  Show the always-on-top timer pill during sessions
                </p>
              </div>
            </div>
            <Switch
              id="floating-enabled"
              checked={prefs.floatingEnabled}
              onCheckedChange={(v) => update({ floatingEnabled: v })}
            />
          </div>

          {/* Keep pinned */}
          <div className={cn(
            'flex items-center justify-between p-4 rounded-lg border transition-opacity',
            !prefs.floatingEnabled && 'opacity-50'
          )}>
            <div className="flex items-start gap-3">
              <Pin className="h-5 w-5 text-primary mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="floating-pinned" className="font-medium">Always keep it visible</Label>
                <p className="text-sm text-muted-foreground">
                  Keep the pill on screen even when no timer is running
                </p>
              </div>
            </div>
            <Switch
              id="floating-pinned"
              checked={prefs.floatingPinned}
              disabled={!prefs.floatingEnabled}
              onCheckedChange={(v) => update({ floatingPinned: v })}
            />
          </div>

          {/* Menu bar countdown */}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-primary mt-0.5" />
              <div className="space-y-0.5">
                <Label htmlFor="menubar-countdown" className="font-medium">Menu bar countdown</Label>
                <p className="text-sm text-muted-foreground">
                  Show the live time next to the menu bar icon
                </p>
              </div>
            </div>
            <Switch
              id="menubar-countdown"
              checked={prefs.menuBarCountdown}
              onCheckedChange={(v) => update({ menuBarCountdown: v })}
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground pt-2">
          The Luma menu bar icon is always available — click it for a quick timer panel.
        </p>

        {/* Mac App Store builds: restore previous purchases */}
        {showRestore && (
          <div className="pt-4 border-t">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleRestore}
              disabled={isRestoring}
            >
              {isRestoring ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Restore Purchases
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
